import type { PlaceRef } from '@/model/DeliveryNote'
import type {
  PassengerTransportDocument,
  PassengerTransportDocumentDeliveryDto,
  PassengerTransportDocumentRequest
} from '@/model/PassengerTransportDocument'
import {
  createEmptyPassengerTransportDocumentDeliveryDto,
  isPassengerTransportDocumentV2,
  SCHEMA_VERSION_V2
} from '@/model/PassengerTransportDocument'

export function mapPassengerTransportDocumentToDeliveryDtos(
  document: PassengerTransportDocument
): PassengerTransportDocumentDeliveryDto[] {
  if (!isPassengerTransportDocumentV2(document) || !document.deliveries?.length) {
    return [createEmptyPassengerTransportDocumentDeliveryDto()]
  }
  return document.deliveries.map((delivery) => ({
    id: delivery.id,
    origins: (delivery.origins ?? []).map((place) => ({
      id: place.id ?? undefined,
      name: place.name ?? undefined
    })),
    destinations: (delivery.destinations ?? []).map((place) => ({
      id: place.id ?? undefined,
      name: place.name ?? undefined
    }))
  }))
}

export function buildV2DeliveriesFromFlatRequest(
  request: PassengerTransportDocumentRequest
): PassengerTransportDocumentDeliveryDto[] {
  return [{
    origins: [{
      id: request.originId,
      name: request.originId ? undefined : request.originName
    }],
    destinations: [{
      id: request.destinationId,
      name: request.destinationId ? undefined : request.destinationName
    }]
  }]
}

export function applyV2DeliveriesForApiRequest(
  request: PassengerTransportDocumentRequest,
  existingDocument?: PassengerTransportDocument,
  v2Enabled = false
): PassengerTransportDocumentRequest {
  if (!v2Enabled) {
    if (existingDocument && isPassengerTransportDocumentV2(existingDocument)) {
      return request
    }
    const { schemaVersion: _schemaVersion, deliveries: _deliveries, ...legacy } = request
    return legacy
  }
  if (existingDocument && !isPassengerTransportDocumentV2(existingDocument)) {
    const { schemaVersion: _schemaVersion, deliveries: _deliveries, ...legacy } = request
    return legacy
  }
  return {
    ...request,
    schemaVersion: SCHEMA_VERSION_V2,
    deliveries: request.deliveries?.length
      ? request.deliveries
      : buildV2DeliveriesFromFlatRequest(request)
  }
}

export function hasValidPassengerTransportDeliveryRoute(
  deliveries: PassengerTransportDocumentDeliveryDto[] | undefined
): boolean {
  if (!deliveries?.length) {
    return false
  }
  return deliveries.some((delivery) => hasPlaceRefs(delivery.origins) && hasPlaceRefs(delivery.destinations))
}

function hasPlaceRefs(refs: PlaceRef[] | undefined): boolean {
  return !!refs?.some((ref) => !!(ref.id?.trim() || ref.name?.trim()))
}
